import { useState } from 'react';

import { CaretDown } from '@phosphor-icons/react';
import { Button } from '@clay-run/glaze/button';
import {
  Dropdown,
  DropdownButtonUnstyled,
  DropdownDescription,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '@clay-run/glaze/dropdown/index';
import { Icon } from '@clay-run/glaze/icon';

import type { SaveMenuOption } from '../usage-sidebar-logic';
import { ConfirmActionDialog } from './ConfirmActionDialog';

export interface SaveMenuProps {
  /** The trigger's own label ("Save") — only shown when there is more than one option; a single option renders as a plain button labeled with ITS OWN label instead. */
  label: string;
  options: readonly SaveMenuOption[];
  disabled: boolean;
  onChoose: (decision: SaveMenuOption['decision']) => void;
}

/**
 * [R6] The ONE Save button every gated in-play edit — and every master
 * doorway — renders its choices through. `saveMenuOptions`
 * (`usage-sidebar-logic.ts`) builds `options`; this component only lays them
 * out and runs the confirm step for an option that carries one.
 *
 * Composition verified against
 * `node_modules/@clay-run/glaze/src/dropdown/dropdown.tsx` the way
 * `KebabMenu.tsx`'s own header documents (this is the SECOND consumer of
 * Glaze's `Dropdown` family in this repo):
 *  - `DropdownButtonUnstyled`'s own `children` prop is NEVER rendered when a
 *    `render` element is supplied — the render element's OWN children are
 *    the real visible content (`base-ui`'s render-prop polymorphism replaces
 *    the rendered output wholesale). The trigger's label therefore has to be
 *    a child of the `render={<Button …>}` element itself, not of
 *    `DropdownButtonUnstyled`. The caret is a second, `aria-hidden` child of
 *    that same `Button` — it must NOT become part of the trigger's accessible
 *    name, which every test (and every rep-visible tooltip) pins to exactly
 *    "Save".
 *  - `DropdownItem` spreads its own `children` onto the rendered `<button>`
 *    unchanged, so a label plus a `DropdownDescription` sibling composes
 *    with no special prop. The label uses `DropdownLabel` (`data-slot="label"`)
 *    rather than a bare `<span>` so a query for the label text alone — the
 *    label and the description are two SEPARATE strings a rep reads as two
 *    lines — resolves to exactly one element even though the description
 *    text is a sibling inside the same button.
 *
 * ── STACKING FIX (2026-09-29, live-QA regression) ────────────────────────
 * This is the FIRST consumer of Glaze's `Dropdown` family that opens inside a
 * `PushDrawer` (`UsageSidebar` renders this in the drawer's footer). The
 * drawer's own stacking contract (`PushDrawer.tsx`'s doc block,
 * `drawer-store.ts`'s `PUSH_DRAWER_PORTAL_CLASS`) only reaches Glaze
 * primitives that NEST their popup into the drawer's own portal node — that
 * nesting is a Base UI mechanism
 * (`@base-ui/react/floating-ui-react/components/FloatingPortal.js`:
 * `containerProp ?? parentPortalNode ?? document.body`), and it is how
 * `Combobox`/`Select`/`Popover` (all Base-UI-portalled) end up sharing the
 * drawer's `z-50` stacking context and painting later in tree order.
 *
 * Glaze's `Dropdown` menu (`dropdown.tsx`'s `DropdownMenu`) portals through
 * `@floating-ui/react`'s OWN `FloatingPortal` instead — a different portal
 * system with no knowledge of Base UI's `parentPortalNode` context. It always
 * creates a fresh node as the LAST CHILD OF `document.body`, a sibling of the
 * drawer's portal, never nested inside it. `DropdownMenu`'s popup carries no
 * z-index of its own (`menuPopup` in `internal/shared-menu/menu-styles.ts` is
 * `isolate w-max …` — `isolate` alone does not set a z-index), so at the body
 * level it sits in the plain "positioned, z-index:auto" stacking layer while
 * the drawer's portal (`PUSH_DRAWER_PORTAL_CLASS = 'relative z-50'`) sits in
 * the positive-z-index layer, which always paints above it regardless of DOM
 * order — confirmed against the product owner's screenshot (the option rows
 * visible only where they stuck out past the drawer's left edge; the
 * overlapping portion was painted BEHIND the opaque drawer panel).
 *
 * Glaze exposes no portal-target/`root` prop on `Dropdown`/`DropdownMenu` to
 * redirect the popup into the drawer's portal node (verified: `DropdownMenu`
 * hardcodes `<FloatingPortal>` with no props), so redirecting the portal is
 * not an option here — the fix is a z-index bump on this popup alone, via the
 * `className` prop `DropdownMenu` already merges with `menuPopup()`. `z-[60]`
 * matches this app's existing "one rung above a `z-50` drawer/sidebar"
 * convention (`EnrollQueueSidebar.tsx`, `GongFlowBrowse.tsx`). No other
 * Glaze `Dropdown` consumer in this repo (`KebabMenu.tsx`, `NavRail.tsx`,
 * `SavedViewsPane.tsx`) opens inside a modal or drawer, so none of them was a
 * working precedent to copy from — this composition is new.
 *
 * `max-w-sm` on the same popup caps its width (`menuPopup`'s own `w-max` has
 * no ceiling, so a long `DropdownDescription` grows the popup instead of
 * wrapping) — the original bug this component's copy work started from.
 *
 * Exactly one option (the in-play ungated case never reaches this component
 * at all — the host writes directly with no gate — but a doorway origin can
 * legally end up with one visible option after a future rule change) renders
 * as a plain primary `Button` with no dropdown: a menu of one item is a
 * pointless extra click.
 *
 * [R9] An option carrying `confirm` (③, "Update plays and the playbook")
 * never calls `onChoose` directly from a menu click — it stages the option
 * and opens `ConfirmActionDialog`. Only Confirm calls `onChoose`; Cancel (or
 * Escape / a backdrop click) closes the dialog and calls nothing.
 */
export function SaveMenu({ label, options, disabled, onChoose }: SaveMenuProps) {
  const [confirming, setConfirming] = useState<SaveMenuOption | null>(null);

  const pick = (option: SaveMenuOption) => {
    if (option.confirm) {
      setConfirming(option);
      return;
    }
    onChoose(option.decision);
  };

  const confirmDialog =
    confirming?.confirm !== undefined ? (
      <ConfirmActionDialog
        open
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={confirming.confirm.title}
        description={confirming.confirm.body}
        confirmLabel={confirming.confirm.confirmLabel}
        pending={false}
        onConfirm={() => {
          onChoose(confirming.decision);
          setConfirming(null);
        }}
      />
    ) : null;

  if (options.length <= 1) {
    const only = options[0];
    return (
      <>
        <Button variant="primary" disabled={disabled} onClick={() => only && pick(only)}>
          {only?.label ?? label}
        </Button>
        {confirmDialog}
      </>
    );
  }

  return (
    <>
      <Dropdown placement="bottom-end">
        <DropdownButtonUnstyled
          render={
            <Button variant="primary" disabled={disabled}>
              {label}
              <Icon type={CaretDown} size="sm" aria-hidden="true" />
            </Button>
          }
        >
          {null}
        </DropdownButtonUnstyled>
        <DropdownMenu className="z-[60] max-w-sm">
          {options.map((option) => (
            <DropdownItem key={option.decision} disabled={disabled} onClick={() => pick(option)}>
              <DropdownLabel>{option.label}</DropdownLabel>
              <DropdownDescription>{option.description}</DropdownDescription>
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
      {confirmDialog}
    </>
  );
}
